import { Injectable } from '@angular/core';
import { JobSectionsService } from '../job-section.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { JobSectionsDaoService } from '../../dao/job-section-dao.service';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import {
  JobSection,
  JobSectionDTO,
  JobSectionLevel,
} from 'src/app/_model/job-section';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { filter, take } from 'rxjs/operators';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';

/**
 * Loads JobSections
 */
@Injectable()
export class JobSectionsLogicService implements JobSectionsService {
  public jobSections$ = new BehaviorSubject<JobSection[]>([]);
  public allJobSections$ = new BehaviorSubject<JobSection[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);
  private currentSjobId: IMSId;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private jobSectionsDaoService: JobSectionsDaoService,
    private authenticationService: AuthenticationService,
    private clientStateService: ClientStateService,
    private remoteActionService: RemoteActionService,
    private servicejobsService: ServicejobsService,
  ) {
    this.initialize();
  }

  public getJobSectionsLevelOne(): JobSection[] {
    return this.jobSections$
      .getValue()
      .filter((jobSection) => jobSection.level === 'LEVEL_1')
      .sort((a, b) =>
        a?.name
          ?.toLocaleLowerCase()
          ?.localeCompare(b?.name?.toLocaleLowerCase()),
      );
  }
  public getJobSectionsLevelTwo(parentJobSection: JobSection): JobSection[] {
    if (parentJobSection === undefined) {
      return [];
    }
    return this.jobSections$
      .getValue()
      .filter(
        (jobSection) =>
          jobSection.level === 'LEVEL_2' &&
          IMSObject.equals(parentJobSection.id, jobSection.parentjobsection.id),
      )
      .sort((a, b) =>
        a?.name
          ?.toLocaleLowerCase()
          ?.localeCompare(b?.name?.toLocaleLowerCase()),
      );
  }
  public getJobSectionsLevelThree(parentJobSection: JobSection): JobSection[] {
    if (parentJobSection === undefined) {
      return [];
    }
    return this.jobSections$
      .getValue()
      .filter(
        (jobSection) =>
          jobSection.level === 'LEVEL_3' &&
          IMSObject.equals(parentJobSection.id, jobSection.parentjobsection.id),
      )
      .sort((a, b) =>
        a?.name
          ?.toLocaleLowerCase()
          ?.localeCompare(b?.name?.toLocaleLowerCase()),
      );
  }
  public getJobSectionsLevelFour(parentJobSection: JobSection): JobSection[] {
    if (parentJobSection === undefined) {
      return [];
    }
    return this.jobSections$
      .getValue()
      .filter(
        (jobSection) =>
          jobSection.level === 'LEVEL_4' &&
          IMSObject.equals(parentJobSection.id, jobSection.parentjobsection.id),
      )
      .sort((a, b) =>
        a?.name
          ?.toLocaleLowerCase()
          ?.localeCompare(b?.name?.toLocaleLowerCase()),
      );
  }

  public getChildJobSection(parentJobSection: JobSection): JobSection[] {
    if (parentJobSection === undefined) {
      return this.getJobSectionsLevelOne();
    }

    switch (parentJobSection.level) {
      case 'LEVEL_1':
        return this.getJobSectionsLevelTwo(parentJobSection);
      case 'LEVEL_2':
        return this.getJobSectionsLevelThree(parentJobSection);
      case 'LEVEL_3':
        return this.getJobSectionsLevelFour(parentJobSection);
      default:
        this.logger.log(
          this.constructor.name,
          'getChildJobSection: Unknown JobSection Level',
          parentJobSection.level,
        );
        return [];
    }
  }

  public getFullJobSectionArray(jobSection: JobSection): JobSection[] {
    if (jobSection === undefined) {
      return [];
    }
    let jobSectionTree: JobSection[] = [];

    jobSectionTree.push(jobSection);
    while (jobSection?.parentjobsection != undefined) {
      jobSection = this.getJobSectionById(jobSection.parentjobsection.id);
      if (jobSection != undefined) {
        jobSectionTree.push(jobSection);
      }
    }

    return jobSectionTree.sort((js1, js2) =>
      js1.level.localeCompare(js2.level),
    );
  }

  public getFullJobSectionLabel(jobSection: JobSection): string {
    return this.getFullJobSectionArray(jobSection)
      .map((js) => js.label)
      .join(' | ');
  }

  public getParentJobSectionWithLevel(
    level: JobSectionLevel,
    jobSection: JobSection,
    exactMatch: boolean = false,
  ): JobSection {
    if (level > jobSection.level) {
      this.logger.warn(
        this.constructor.name,
        'getParentJobSectionWithLevel where level is greater or equal the provided jobsections level',
        level,
        jobSection,
      );
      return exactMatch ? undefined : jobSection;
    }

    return this.getFullJobSectionArray(jobSection).find(
      (js) => js.level === level,
    );
  }

  // Public Function
  public getJobSectionById(id: IMSId): JobSection {
    if (id === undefined) {
      return undefined;
    }

    return this.jobSections$.getValue().find((jobSection) => {
      if (
        jobSection.id.imsId == id.imsId &&
        jobSection.id.secondaryId == id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getJobSectionsByServicejobId(sjobId: IMSId): JobSection[] {
    if (sjobId === undefined) {
      return [];
    }

    return this.allJobSections$.getValue().filter((oi) => {
      if (oi.servicejob?.id?.imsId === sjobId.imsId) {
        return true;
      }
      return false;
    });
  }

  public async updateJobSections(jobSectionsDto: JobSectionDTO[]) {
    this.logger.log(
      this.constructor.name,
      '- Received new jobsections from other IMS component',
      jobSectionsDto,
    );

    try {
      let jobSections: JobSection[] = [];

      for (const jobSectionDto of jobSectionsDto) {
        jobSections.push(JobSection.fromDto(jobSectionDto));
      }

      await this.jobSectionsDaoService.saveJobSections(jobSections);
      await this.loadLocalJobSections(this.currentSjobId);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing JobSections: ',
        error.message,
      );
      throw error;
    }
  }

  // Resetable
  public async reset() {
    await this.jobSectionsDaoService.reset();
    await this.loadLocalJobSections(undefined);
  }

  public async clear() {
    await this.jobSectionsDaoService.clear();
    this.jobSections$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemoteJobSections(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}

  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.clientStateService.initializeStatus$
        .pipe(
          filter((status) => status === InitializeStatus.INITIALIZED),
          take(1),
        )
        .toPromise();

      console.log(
        'JBJSUpdate initialize of clientstate finished load js for',
        this.clientStateService.theState.getValue()?.serviceJobId,
      );
      await this.loadLocalJobSections(
        this.clientStateService.theState.getValue()?.serviceJobId,
      );
      this.clientStateService.theState.subscribe({
        next: async (state) => {
          console.log(
            'JBJSUpdate received sjobid',
            state?.serviceJobId?.imsId,
            'current',
            this.currentSjobId?.imsId,
          );
          if (
            !IMSObject.equals(this.currentSjobId, state?.serviceJobId) ||
            (this.currentSjobId == undefined &&
              state?.serviceJobId == undefined)
          ) {
            console.log(
              'JBJSUpdate update currentSjobId to',
              this.currentSjobId,
            );
            // this.currentSjobId = state?.serviceJobId;
            await this.loadLocalJobSections(state?.serviceJobId);
          }
        },
      });
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalJobSections(sjobId: IMSId) {
    try {
      let jobSections: JobSection[] =
        await this.jobSectionsDaoService.getJobSectionsForServiceJob(sjobId);
      let allJobSections: JobSection[] =
        await this.jobSectionsDaoService.getJobSections();
      this.logger.log(
        this.constructor.name,
        '- Loaded local JobSections for currentJob',
        this.clientStateService.theState.getValue().serviceJobId,
        sjobId,
        jobSections.length,
        ' - all available',
        allJobSections.length,
      );
      this.currentSjobId = sjobId;
      this.jobSections$.next(jobSections);
      this.allJobSections$.next(allJobSections);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local jobsections: ',
        error.message,
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {}

  private async loadRemoteJobSections(apiLevel: APILevel, syncKey: string) {
    this.logger.log(
      this.constructor.name,
      '- loadRemoteJobSections with APILevel',
      apiLevel,
    );
    if (apiLevel < APILevel.DEDICATED_JOBSECTIONS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load dedicated jobsections - do nothing jobsections will be loaded with sjob sync',
      );
      return;
    }
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new JobSections from IMS');
      await this.jobSectionsDaoService.clear();
      let newJobSections: JobSection[] = await this.loadRemoteJobSectionBatch(
        syncKey, []
      );
      this.logger.log(
        this.constructor.name,
        'loadRemoteJobSections - Finished loading' +
          newJobSections.length +
          ' new jobsections',
      );
      await this.loadLocalJobSections(this.currentSjobId);
    }
  }

  private async loadRemoteJobSectionBatch(
    syncKey: string,
    jobSections: JobSection[],
    nextPart?: string,
  ): Promise<JobSection[]> {
    console.log(
      this.constructor.name,
      'loadRemoteJobSectionBatch - start loading batch with current JobSections',
      jobSections?.length,
      'and nextPart',
      nextPart,
    );
    let jobSectionsData;

    if (nextPart) {
      jobSectionsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey
          },
        }),
      );

      console.log(
        this.constructor.name,
        'loadRemoteJobSectionBatch - received jobsections with nextpart request with',
        jobSectionsData?.length,
        'jobsections',
      );
    } else {
      const sjobList = this.servicejobsService.servicejobs$
        .getValue()
        .map((sjob) => {
          return sjob.getImsId();
        })
        .join(',');

      let remoteActionData = {
        endpoint: 'syncjobsections',
        cachable: false,
        fifo: false,
        authenticationRequired: true,
        data: {
          sjoblist: sjobList,
          synckey: syncKey
        },
      };

      jobSectionsData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction(remoteActionData),
      );

      console.log(
        this.constructor.name,
        'loadRemoteJobSectionBatch - received jobsectionsDataContainer with',
        jobSectionsData?.length,
        'jobSections',
      );
    }

    let nextId: string;
    let nextJobSections: JobSection[] = [];
    for (const jobSectionData of jobSectionsData) {
      if (jobSectionData['#nextid']) {
        nextId = jobSectionData['#nextid'];
      } else {
        nextJobSections.push(JobSection.fromDto(jobSectionData, undefined));
      }
    }

    await this.jobSectionsDaoService.saveJobSections(nextJobSections);

    let returnJobSections = jobSections.concat(nextJobSections);
    if (nextId) {
      console.log(
        this.constructor.name,
        'loadRemoteJobSectionBatch - Continue to download more jobsections with nextid',
        nextId,
      );
      return await this.loadRemoteJobSectionBatch(syncKey, returnJobSections, nextId);
    }

    return returnJobSections;
  }
}
